import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { ReactNode } from 'react';

// Prototype `toast(msg)`: one message at a time, shown for 3.2 s.

export const TOAST_MS = 3200;

const ToastContext = createContext<((text: string) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [text, setText] = useState('');
  const [shown, setShown] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const toast = useCallback((next: string) => {
    setText(next);
    setShown(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setShown(false), TOAST_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => toast, [toast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={`toast${shown ? ' show' : ''}`} id="toast" role="status" aria-live="polite">
        {text}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): (text: string) => void {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error('useToast needs a ToastProvider.');
  return toast;
}
